/**
 * Клиент Chatwoot (наша копия 4.15).
 *
 * Вход — обмен пропуска UNIA на ключи Chatwoot одним запросом, без пароля:
 *   POST {CHATWOOT_URL}/api/v1/unia_sso/credentials
 *   Authorization: Bearer <access_token Supabase>
 *   → 200 { headers: { access-token, token-type, client, expiry, uid },
 *           user: { id, email, name }, api_host }
 *   → 401 { error: 'invalid-token' } | 422 { error: 'no-email' }
 * (_vendor/chatwoot/app/controllers/unia_sso_controller.rb, #credentials).
 * Путь с /api/ — не украшение: междоменное разрешение (CORS) Chatwoot выдаёт
 * именно ему, короткий /unia_sso/credentials из другого источника не прочтётся.
 *
 * 🪤 Ходим ТОЛЬКО по публичному https-адресу (FORCE_SSL): внутренний http
 * отвечает 308, и запрос теряется.
 */
import config from '@/lib/config';
import { getFreshAccessToken } from '@/lib/authToken';
import { ApiError, fetchWithTimeout, reasonOf } from '@/lib/http';
import { useChatwootStore, type ChatwootHeaders, type ChatwootUser } from '@/store/chatwoot';

interface ОтветОбмена {
  headers?: Partial<ChatwootHeaders>;
  user?: ChatwootUser;
  api_host?: string;
}

let обменИдёт: Promise<void> | null = null;

/** Обмен пропуска UNIA на пропуск Chatwoot. Параллельные вызовы сводятся в один. */
export function exchangeCredentials(): Promise<void> {
  if (!обменИдёт) {
    обменИдёт = (async () => {
      const token = await getFreshAccessToken();
      if (!token) throw new ApiError(401, 'Нет действующего входа в UNIA');
      const res = await fetchWithTimeout(`${config.CHATWOOT_URL}/api/v1/unia_sso/credentials`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new ApiError(res.status, await reasonOf(res));
      const body = (await res.json()) as ОтветОбмена;
      const h = body.headers;
      if (!h?.['access-token'] || !h.client || !h.uid) {
        throw new ApiError(502, 'Chatwoot вернул неполный пропуск');
      }
      useChatwootStore.getState().setSession(
        {
          'access-token': h['access-token'],
          client: h.client,
          uid: h.uid,
          expiry: h.expiry,
          'token-type': h['token-type'] ?? 'Bearer',
        },
        body.user ?? null,
      );
    })().finally(() => { обменИдёт = null; });
  }
  return обменИдёт;
}

/** Chatwoot ротирует пропуск — подхватываем свежий из заголовков ответа. */
function подхватитьРотацию(res: Response): void {
  const next = res.headers.get('access-token');
  if (!next) return;
  const cur = useChatwootStore.getState().headers;
  if (!cur) return;
  useChatwootStore.getState().setHeaders({
    ...cur,
    'access-token': next,
    client: res.headers.get('client') || cur.client,
    uid: res.headers.get('uid') || cur.uid,
    expiry: res.headers.get('expiry') || cur.expiry,
  });
}

/** Запрос к API Chatwoot. На 401 один раз обмениваем пропуск заново и повторяем. */
export async function cwFetch<T>(path: string, init: RequestInit = {}, повтор = true, ms = 20_000): Promise<T> {
  if (!useChatwootStore.getState().headers) await exchangeCredentials();
  const h = useChatwootStore.getState().headers;
  if (!h) throw new ApiError(401, 'Нет пропуска Chatwoot');

  const res = await fetchWithTimeout(`${config.CHATWOOT_URL}${path}`, {
    ...init,
    headers: {
      Accept: 'application/json',
      ...(init.headers as Record<string, string> | undefined),
      'access-token': h['access-token'],
      client: h.client,
      uid: h.uid,
      'token-type': h['token-type'] ?? 'Bearer',
    },
  }, ms);
  подхватитьРотацию(res);

  if (res.status === 401 && повтор) {
    useChatwootStore.getState().clear();
    await exchangeCredentials();
    return cwFetch<T>(path, init, false, ms);
  }
  if (!res.ok) throw new ApiError(res.status, await reasonOf(res));
  // Часть ручек (update_last_seen, mute…) отвечает пустым телом: res.json() на нём падает.
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

// ── Типы и вызовы для проверочных экранов (дальше этапы 2+ добавят остальное) ──

export interface CwAccountRef {
  id: number;
  name: string;
  role?: string;
}

export interface CwProfile {
  id: number;
  name: string;
  email: string;
  accounts: CwAccountRef[];
}

export interface CwMeta {
  mine_count: number;
  assigned_count: number;
  unassigned_count: number;
  all_count: number;
}

export interface CwInbox {
  id: number;
  name: string;
  channel_type: string;
}

export const getProfile = () => cwFetch<CwProfile>('/api/v1/profile');

export async function getConversationsMeta(accountId: number): Promise<CwMeta> {
  const r = await cwFetch<{ meta: CwMeta }>(`/api/v1/accounts/${accountId}/conversations/meta`);
  return r.meta;
}

export async function getInboxes(accountId: number): Promise<CwInbox[]> {
  const r = await cwFetch<{ payload: CwInbox[] }>(`/api/v1/accounts/${accountId}/inboxes`);
  return r.payload;
}
